import {
  convertDashedUSPhoneNumberToInternationalFormat,
  DateFormat,
} from '@equip.health/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { buildUniversalSelectSearchTerm } from './inquiry.util';
import {
  convertPhoneNumberToInternationalFormat,
  getName,
} from './patient.util';
import util from './util';
import { validateEmail } from '../common';
import {
  MaudPermissions,
  MaudPermissionsMap,
  MaudRoles,
  ProviderAccess,
  ROLE_WITH_EDIT_FAMILY_TASK_PERMISSION,
  OpsPermissions,
  OpsPermissionsMap,
} from '../constants/permissions.constants';
import { UserStatus } from '../constants/user.constants';
import fileUtil from './file.util';
import {
  FILE_SIZE_INVALID_MESSAGE,
  FILE_SIZE_INVALID_MESSAGE_AFTER_EDIT,
  INVALID_FILE_TYPE_MESSAGE,
  MAX_FILE_SIZE_BYTES,
  MIN_FILE_SIZE_BYTES,
  PROFILE_ACCEPTED_FILE_EXTENSIONS,
} from '../constants/profile.constants';
import { DateTime } from 'luxon';
import { AppAccess } from '../constants/global.constants';

export enum ProfileTab {
  PROFILE,
  CALENDAR,
}

export const ProfileTabsCollection: Record<ProfileTab, TabDefinition> = {
  [ProfileTab.PROFILE]: {
    label: 'Profile',
    subPath: 'info',
  },
  [ProfileTab.CALENDAR]: {
    label: 'Calendar',
    subPath: 'calendar',
  },
};

export const getTabIndex = (path: string): number => {
  const idx = Object.values(ProfileTabsCollection).findIndex(
    ({ subPath }: TabDefinition) => subPath === path,
  );
  return Math.max(idx, ProfileTab.PROFILE);
};

export const credentialsEnumMenuOptionsMapper = (
  options: MultiSelectOptions[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ code, name }: MultiSelectOptions) => ({
    label: `${name} (${code})`,
    value: code,
  }));
};

export const providerEnumMenuOptionsMapper = (
  options: MultiSelectOptions[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ label, code }: MultiSelectOptions) => ({
    label,
    searchData: buildUniversalSelectSearchTerm(label),
    value: code,
  }));
};

export const providerTimezoneEnumMenuOptionsMapper = (
  options: MultiSelectOptions[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ label, id }: MultiSelectOptions) => ({
    label,
    searchData: buildUniversalSelectSearchTerm(label),
    value: id,
  }));
};
export const providerStatesEnumMenuOptionsMapper = (
  options: { label: string; externalId: string }[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ label, externalId }) => ({
    label,
    searchData: buildUniversalSelectSearchTerm(label),
    value: externalId,
  }));
};
export const providerLanguagesEnumMenuOptionsMapper = (
  options: MultiSelectOptions[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ label, id }: MultiSelectOptions) => ({
    label,
    searchData: buildUniversalSelectSearchTerm(label),
    value: id,
  }));
};

export const getMultiSelectedUniversalSelectOptions = (
  selectedValues: string[],
  options: UniversalSelectOption[],
): UniversalSelectOption[] => {
  return options.filter((option) => selectedValues?.includes(option.value));
};

export const getSupervisorFullName = (
  firstName?: string,
  lastName?: string,
): string => {
  const last = getName(lastName);
  const first = getName(firstName);
  return getName(`${first} ${last}`);
};

export const getOpsPermissionDisplayValues = (
  permissions: string[],
): string[] =>
  (permissions ?? [])
    .filter((permission): permission is OpsPermissions =>
      Object.values(OpsPermissions).includes(permission as OpsPermissions),
    )
    .map((permission) => OpsPermissionsMap[permission].display);

export const getMaudPermissionDisplayValues = (
  permissions: string[],
): string[] => {
  const permissionValues = permissions
    ?.map((permissionString) => {
      const enumKey = Object.keys(MaudPermissions).find(
        (key) =>
          MaudPermissions[key as keyof typeof MaudPermissions] ===
          permissionString,
      );
      if (!enumKey) return null;

      const {
        display,
        addendum,
        index = Number.MAX_SAFE_INTEGER,
      } = MaudPermissionsMap[
        MaudPermissions[enumKey as keyof typeof MaudPermissions]
      ];
      return {
        displayValue: addendum ? `${display} ${addendum}` : display,
        index,
      };
    })
    .filter(Boolean);
  return permissionValues
    ?.sort((a, b) => a.index - b.index)
    ?.map((item) => item.displayValue);
};
export const isDataEmpty = (data: string): boolean => {
  return !data || data.trim().length <= 0;
};

export const isCharacterCountExceeded = (
  data: string,
  limit: number,
): boolean => {
  return data?.trim().length > limit;
};

export const isPhoneNumberValid = (phoneNumber: string): boolean => {
  const unformattedPhoneNumber =
    convertDashedUSPhoneNumberToInternationalFormat(phoneNumber);
  return util.validatePhoneNumber(unformattedPhoneNumber);
};

export const enforceMinMaxBoundsOnHoursPerWeek = (value: string): string => {
  const hoursNumber = parseInt(value, 10);
  if (hoursNumber === 0) return '1';
  if (hoursNumber > 168) return '168';
  return hoursNumber.toString();
};

export const isNPIValid = (npi: string | null | undefined): boolean => {
  if (!npi) return true;
  const NPI_LENGTH = 10;
  return npi.length === NPI_LENGTH && /^\d{10}$/.test(npi);
};

export const ERROR_MESSAGES = {
  DOB: 'Please enter a valid date of birth',
  EMAIL: 'Please enter a valid email',
  MRN_LIMIT: 'Text exceeds 60 character limit',
  NAME_LIMIT: 'Text exceeds 75 character limit',
  PHONE: 'Please enter a valid phone number',
  PRONUNCIATION_LIMIT: 'Text exceeds 100 character limit',
};

export const shouldDisableSaveButton = (editForm: EditProfile) => {
  if (!editForm) return true;
  const fieldsToCheckForCharacterLimit = [
    editForm?.firstName,
    editForm?.middleName,
    editForm?.lastName,
    editForm?.suffix,
    editForm?.displayFirstName,
    editForm?.displayMiddleName,
    editForm?.displayLastName,
    editForm?.displaySuffix,
    editForm?.otherPronounText,
  ];
  const hasExceededCharLimit = fieldsToCheckForCharacterLimit.some((field) =>
    isCharacterCountExceeded(field, 75),
  );
  return (
    hasExceededCharLimit ||
    isCharacterCountExceeded(editForm?.pronunciation, 100) ||
    (editForm?.email && !validateEmail(editForm?.email)) ||
    (editForm?.phoneNumber && !isPhoneNumberValid(editForm?.phoneNumber)) ||
    (editForm?.nationalProviderIdentifier &&
      !isNPIValid(editForm?.nationalProviderIdentifier))
  );
};

export const validateUserErrors = (
  editForm: EditProfile,
  showDisplayName: boolean,
) => {
  const userErrors = {
    displayFirstName:
      showDisplayName && isDataEmpty(editForm?.displayFirstName),
    displayLastName: showDisplayName && isDataEmpty(editForm?.displayLastName),
    otherPronounText:
      editForm?.pronouns?.includes('OTHER') &&
      isDataEmpty(editForm?.otherPronounText),
    phoneNumber: isDataEmpty(editForm?.phoneNumber),
  };
  return userErrors;
};

export const validateAdminErrors = (
  editForm: EditProfile,
  showDisplayName: boolean,
) => {
  const errors = {
    capacity: isDataEmpty(editForm?.capacity),
    displayFirstName:
      showDisplayName && isDataEmpty(editForm?.displayFirstName),
    displayLastName: showDisplayName && isDataEmpty(editForm?.displayLastName),
    email: isDataEmpty(editForm?.email),
    firstName: isDataEmpty(editForm?.firstName),
    hoursPerWeek:
      editForm?.capacity === 'PARTTIME' &&
      isDataEmpty(String(editForm?.hoursPerWeek)),
    lastName: isDataEmpty(editForm?.lastName),
    otherPronounText:
      editForm?.pronouns?.includes('OTHER') &&
      isDataEmpty(editForm?.otherPronounText),
    phoneNumber: isDataEmpty(editForm?.phoneNumber),
    providerType: isDataEmpty(editForm?.providerType),
  };
  return errors;
};

export const getEditPayloadData = (editForm: EditProfile) => {
  const updatedFormFields = {
    connectWithMe: editForm?.connectWithMe?.trim(),
    credentials: editForm?.credentials?.map((credential) => ({
      code: credential,
    })),
    displayFirstName: editForm.displayFirstName?.trim(),
    displayLastName: editForm.displayLastName?.trim(),
    displayMiddleName: editForm.displayMiddleName?.trim(),
    displaySuffix: editForm.displaySuffix?.trim(),
    languages: editForm?.languages?.map((language) => ({
      externalId: language,
    })),
    otherPronounText: editForm?.pronouns?.includes('OTHER')
      ? editForm?.otherPronounText?.trim()
      : '',
    phoneNumber: convertPhoneNumberToInternationalFormat(
      editForm?.phoneNumber,
    )?.replace(/\D/g, ''), // We need to use .replace(/\D/g, '') here because the API only accepts digits and does not accept the '+' sign.
    professionalExperience: editForm?.professionalExperience?.trim(),
    pronouns: editForm?.pronouns?.map((pronoun) => ({
      code: pronoun,
    })),
    pronunciation: editForm?.pronunciation?.trim(),
  };
  return updatedFormFields;
};

export const getPayloadDataForAdmin = (
  isAddForm: boolean,
  editForm: EditProfile,
  role: string,
  isProviderTypePkTransitionEnabled: boolean,
): AddEditProviderRequestBody => {
  const capacityCode =
    editForm?.capacity === 'PARTTIME' || editForm?.capacity === 'FULLTIME'
      ? editForm?.capacity
      : null;
  const data = {
    accountStatus: isAddForm ? UserStatus.ACTIVE : editForm?.accountStatus,
    appAccess: isAddForm ? [AppAccess.MAUD] : editForm?.appAccess,
    capacity: {
      code: capacityCode as 'PARTTIME' | 'FULLTIME',
    },
    email: editForm?.email?.trim(),
    firstName: editForm?.firstName?.trim(),
    hoursPerWeek: Number(editForm?.hoursPerWeek) || null,
    lastName: editForm?.lastName?.trim(),
    middleName: editForm?.middleName?.trim(),
    nationalProviderIdentifier: editForm?.nationalProviderIdentifier || null,
    permissions: isAddForm
      ? [
          ProviderAccess.MAUD_ACCESS_APP,
          MaudPermissions.MAUD_PATIENT_SEARCH,
          MaudPermissions.MAUD_PATIENT_DASHBOARD,
          MaudPermissions.MAUD_AVAILABILITY,
          ...(ROLE_WITH_EDIT_FAMILY_TASK_PERMISSION?.includes(role)
            ? [MaudPermissions.MAUD_EDIT_FAMILY_TASK]
            : []),
        ]
      : editForm?.permissions,
    phoneNumber: convertPhoneNumberToInternationalFormat(
      editForm?.phoneNumber,
    )?.replace(/\D/g, ''), // We need to use .replace(/\D/g, '') here because the API only accepts digits and does not accept the '+' sign.,
    providerType: isProviderTypePkTransitionEnabled
      ? { code: editForm?.providerType }
      : { externalId: editForm?.providerType },
    roles: isAddForm ? [MaudRoles.MAUD_GENERAL_ACCESS] : editForm?.roles,
    stateOfResidence: { code: editForm?.stateOfResidence },
    suffix: editForm?.suffix?.trim(),
    supervisor: { externalId: editForm?.supervisor },
    timezone: { code: editForm?.timezone },
  };
  return data;
};

export const getDuplicateErrorForEmailAndPhone = (
  error: FetchBaseQueryError | SerializedError,
): { message: string } | undefined => {
  if ((error as FetchBaseQueryError)?.status === 409) {
    const data = (error as FetchBaseQueryError).data as { message: string };
    return data?.message ? { message: data.message } : undefined;
  }
  return undefined;
};

export const reformatErrorMessageForDisplay = (message: string): string => {
  const uuidPattern =
    /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12} - /;
  return message?.replace(uuidPattern, '');
};

export const validateFile = (
  file: File,
  isAfterEdit: boolean = false,
): string | null => {
  const fileExtension = fileUtil.getFileExtension(file.name.toLowerCase());
  const fileSizeBytes = file.size;
  if (
    !isAfterEdit &&
    !PROFILE_ACCEPTED_FILE_EXTENSIONS.includes(fileExtension)
  ) {
    return INVALID_FILE_TYPE_MESSAGE;
  }
  if (
    fileSizeBytes < MIN_FILE_SIZE_BYTES ||
    fileSizeBytes > MAX_FILE_SIZE_BYTES
  ) {
    return isAfterEdit
      ? FILE_SIZE_INVALID_MESSAGE_AFTER_EDIT
      : FILE_SIZE_INVALID_MESSAGE;
  }
  return null;
};
export const getLatestOutOfOfficeEndDate = (
  data: OutOfOfficeItem[],
): string | null => {
  if (!data || data.length === 0) return null;
  const currentDate = DateTime.now().startOf('day');

  try {
    const sortedEntries = [...data].sort((a, b) => {
      const aDate = DateTime.fromISO(a.startDate).startOf('day');
      const bDate = DateTime.fromISO(b.startDate).startOf('day');
      if (!aDate.isValid || !bDate.isValid) {
        throw new Error('Invalid date format in sorting');
      }
      return aDate.toMillis() - bDate.toMillis();
    });

    let latestEndDate: DateTime | null = null;
    let activeEndDate: DateTime | null = null;

    for (const entry of sortedEntries) {
      const entryStartDate = DateTime.fromISO(entry.startDate).startOf('day');
      const entryEndDate = DateTime.fromISO(entry.endDate).startOf('day');

      if (!entryStartDate.isValid || !entryEndDate.isValid) {
        throw new Error('Invalid date format in entry');
      }

      if (entryStartDate <= currentDate) {
        if (!activeEndDate || entryEndDate > activeEndDate) {
          activeEndDate = entryEndDate;
        }
        if (!latestEndDate || entryEndDate > latestEndDate) {
          latestEndDate = entryEndDate;
        }
      } else if (activeEndDate && entryStartDate <= activeEndDate) {
        if (entryEndDate > latestEndDate!) {
          latestEndDate = entryEndDate;
        }
      } else {
        break;
      }
    }

    return latestEndDate
      ? latestEndDate.toFormat(DateFormat.fullMonthDayYear)
      : null;
  } catch (error) {
    console.error('Error processing out-of-office dates:', error);
    return null;
  }
};
